/**
 * R17 A01 (STATE-HIST) — `history/begin-batch` + `history/commit-batch`,
 * extracted VERBATIM from `editorStore.ts`'s `dispatch()` body (no behaviour
 * change). Same STORE-SPLIT lineage as `editorStoreHistoryUndoRedo.ts`
 * (companion module — see its header for the `set`/`get` ≡
 * `useEditorStore.getState()` equivalence and the shared COLLAB-LOCK-COMMAND
 * veto-aware history rationale that also governs
 * `editorStoreHistoryReplay.ts`'s abort-batch/replay-forwards/replay-inverses).
 *
 * `editorStore.ts`'s `dispatch()` still OWNS the `command.type ===
 * "history/begin-batch" | "history/commit-batch"` routing; it delegates each
 * branch's BODY to `handleHistoryBeginBatch` / `handleHistoryCommitBatch`
 * below, then returns. Neither handler references `command` (the original
 * bodies never read it either), so only `set` is threaded through.
 */
import {
  bumpForClasses,
  EMPTY_BUMP_CLASSES,
  unionBumpClasses,
} from "@dxf-ai-editor/drawing-model";
import {
  type DynamicBlockCommand,
  type EditorCommand,
  type HistoryBatch,
  type HistoryEntry,
} from "@dxf-ai-editor/contracts";
import { materializeDocumentSpacesConcretely } from "./spaceReadThrough";
// R17 A01 (STATE-HIST) — the host-store surface + `set`/`get` type aliases
// are declared ONCE in `editorStoreHistoryUndoRedo.ts` (as
// `HistoryOrchestrationHostStore`/`…Set`/`…Get`) and re-used here, rather
// than a second near-identical narrower declaration — the jscpd Этап-1 gate
// flagged the original three-way duplication of this exact boilerplate
// shape, so consolidating to one canonical declaration is the fix, not a
// baseline. No cycle: `editorStoreHistoryUndoRedo.ts` does not import this
// file back.
import {
  type HistoryOrchestrationSet,
} from "./editorStoreHistoryUndoRedo";

// ── Phase 4.1.f — history/begin-batch ────────────────────────────
// Opens a new batch (depth=1) or increments depth for nested batches.
export function handleHistoryBeginBatch(
  command: Extract<
    EditorCommand | DynamicBlockCommand,
    { type: "history/begin-batch" }
  >,
  set: HistoryOrchestrationSet,
): void {
  set((s) => {
    const { current } = s.state.history;
    const nextCurrent: HistoryBatch =
      current === null
        ? {
            label: command.label,
            forwards: [],
            inverses: [],
            selectionBefore: [...s.state.selection.entityIds],
            spaceId: s.state.drawing.activeSpaceId,
            startedAt: Date.now(),
            depth: 1,
          }
        : { ...current, depth: current.depth + 1 };
    return {
      state: {
        ...s.state,
        history: { ...s.state.history, current: nextCurrent },
      },
      // ABB-S1 — start fresh per-batch accumulators on the OUTERMOST begin;
      // nested begins keep accumulating into the open sets. round-3 F2 — the
      // direct-set accumulator is started alongside the batched-command one.
      // round-4 GENERALIZATION 2 — also snapshot the pre-batch BASE doc +
      // shell state (by reference; both immutable) so `abort-batch` can diff
      // the NET surviving delta. Nested begins keep the outer base.
      // PR #362 round-5 (F3 ROOT) — freeze the pre-batch BASE doc to CONCRETE
      // entity arrays. `s.geometryDocument` is a read-through wrapper; the
      // batch's commands mutate the live cache the wrapper reads through, so a
      // wrapper base would read POST-mutation entities at abort-diff time and
      // miss any geometry that PERSISTED past abort (null-inverse forward).
      ...(current === null
        ? {
            pendingBumpClasses: EMPTY_BUMP_CLASSES,
            pendingDirectSetBumpClasses: EMPTY_BUMP_CLASSES,
            pendingBatchBaseDoc: materializeDocumentSpacesConcretely(
              s.geometryDocument,
            ),
            pendingBatchBaseState: s.state,
            // Gate #4 / FIX#1 — capture the pre-batch audit high-water so
            // `abort-batch` can REWIND the audit ring to it (symmetric with
            // how the geometry is reverted). Captured by value on the
            // OUTERMOST begin; nested begins keep the outer base.
            pendingBatchBaseAuditSequence: s.state.audit.nextSequence,
          }
        : {}),
    };
  });
}

// ── Phase 4.1.f — history/commit-batch ───────────────────────────
// depth>1: decrement (nested commit). depth===1: flatten to one entry.
export function handleHistoryCommitBatch(set: HistoryOrchestrationSet): void {
  set((s) => {
    const { current } = s.state.history;
    if (current === null) return s; // nothing to commit

    if (current.depth > 1) {
      return {
        state: {
          ...s.state,
          history: {
            ...s.state.history,
            current: { ...current, depth: current.depth - 1 },
          },
        },
      };
    }

    // depth === 1: commit the batch.
    if (current.forwards.length === 0) {
      // No HISTORY entry added (no invertible geometry forward), but a batch
      // whose only effects are selection / context / pure-table changes still
      // accumulated bump classes into `pendingBumpClasses` (the diff at the
      // chokepoint folds context/selection commands that never produce a
      // `forwards` entry). ABB-S1 P1-A: DUAL-BUMP from the accumulator here —
      // `bumpForClasses` is a no-op on the EMPTY set, so a TRULY empty batch
      // (begin→commit with nothing between) still advances no counter, while a
      // selection-only / pure-context-switch AI turn (AI turns are always
      // begin/commit-batch-wrapped) is no longer silently dropped.
      // round-3 F2 — fold BOTH the batched-command accumulator AND the
      // direct-set (survives-abort) accumulator for the turn's single bump.
      const accumulated = unionBumpClasses(
        s.pendingBumpClasses ?? EMPTY_BUMP_CLASSES,
        s.pendingDirectSetBumpClasses ?? EMPTY_BUMP_CLASSES,
      );
      return {
        state: {
          ...s.state,
          history: { ...s.state.history, current: null },
        },
        revisions: bumpForClasses(s.revisions, accumulated),
        pendingBumpClasses: null,
        pendingDirectSetBumpClasses: null,
        // round-4 GENERALIZATION 2 — release the abort-only base snapshot.
        pendingBatchBaseDoc: null,
        pendingBatchBaseState: null,
        // Gate #4 / FIX#1 — commit KEEPS the audit ring (the committed turn's
        // events legitimately stream); just release the abort-only base.
        pendingBatchBaseAuditSequence: null,
        // Gate #4 round-2 (FIX C) — EXPLICIT commit close: the subscriber
        // flushes this turn's staged telemetry.
        lastBatchClosedBy: "commit",
      };
    }

    // ABB-S1 — DUAL-BUMP once for the whole turn: snapshotRevision + each
    // finer sibling whose class appeared in the accumulated batch. Cleared
    // after. `?? EMPTY` defends a commit with no recorded begin accumulator.
    // round-3 F2 — fold the direct-set (survives-abort) accumulator too.
    const committedRevisions = bumpForClasses(
      s.revisions,
      unionBumpClasses(
        s.pendingBumpClasses ?? EMPTY_BUMP_CLASSES,
        s.pendingDirectSetBumpClasses ?? EMPTY_BUMP_CLASSES,
      ),
    );

    const now = Date.now();
    const entry: HistoryEntry = {
      id: now.toString(),
      forward: {
        type: "history/replay-forwards" as const,
        source: "system" as const,
        batch: [...current.forwards],
        // P1 (post-review, round 6, finding #15) — cross-reference the
        // OTHER half of this same entry so a partially-applied REDO can
        // roll back its applied prefix via each applied forward's OWN
        // known inverse — see the `history/replay-forwards` handler's
        // doc comment for the exact index math.
        siblingBatch: [...current.inverses],
      },
      inverse: {
        type: "history/replay-inverses" as const,
        source: "system" as const,
        batch: [...current.inverses],
        // Mirror of the above: a partially-applied UNDO rolls back via
        // each applied inverse's OWN known forward.
        siblingBatch: [...current.forwards],
      },
      summary: current.label,
      spaceId: current.spaceId,
      selectionBefore: [...current.selectionBefore],
      selectionAfter: [...s.state.selection.entityIds],
      timestamp: now,
    };
    const rawPast = [...s.state.history.past, entry];
    const cap = s.state.history.depthCap;
    const cappedPast =
      cap === 0
        ? []
        : rawPast.length > cap
          ? rawPast.slice(rawPast.length - cap)
          : rawPast;
    const dropped = rawPast.length - cappedPast.length;
    return {
      state: {
        ...s.state,
        history: {
          ...s.state.history,
          past: cappedPast,
          future: [],
          current: null,
          droppedCount: s.state.history.droppedCount + dropped,
        },
      },
      revisions: committedRevisions,
      pendingBumpClasses: null,
      pendingDirectSetBumpClasses: null,
      // round-4 GENERALIZATION 2 — release the abort-only base snapshot.
      pendingBatchBaseDoc: null,
      pendingBatchBaseState: null,
      // Gate #4 / FIX#1 — commit KEEPS the audit ring; release the base only.
      pendingBatchBaseAuditSequence: null,
      // Gate #4 round-2 (FIX C) — EXPLICIT commit close: the subscriber
      // flushes this turn's staged telemetry.
      lastBatchClosedBy: "commit",
    };
  });
}
